import React, { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ja';
import { useDispatch, useSelector } from 'react-redux';
import { useErrorHandler } from 'react-error-boundary';

import selectDate from '../../api/reserve/selectDate';
import { useDidMount, useNavigateWithId } from '../../customHooks';
import TopicPath from '../../components/TopicPath';
import SelectionSummary from '../../components/SelectionSummary';
import BackButton from '../../components/BackButton';
import { saveTimearea, clearDate } from '../../redux_toolkit/reserveSlice';
import MoveTop from '../../CommonParts';
import { CANCEL_ANNOUNCE } from '../../constants';

dayjs.extend(utc);
dayjs.extend(localeData);
dayjs.extend(weekday);
dayjs.locale('ja');

const SelectDate = function () {
  const navigate = useNavigateWithId();
  const dispatch = useDispatch();
  const handleError = useErrorHandler();

  const rsvConfig = useSelector((state) => state.rsvConfig);
  const { topicPath = [] } = rsvConfig;

  const isLoggedIn = useSelector((state) => state.login.accessToken);

  const lgCd = useSelector((state) => state.reserve.lgCd);
  const rsvSetId = useSelector((state) => state.reserve.rsvSetId);
  const [weekInfo, setWeekInfo] = useState([]);
  const [displayWeeks, setDisplayWeeks] = useState(1);
  const [daysList, setDaysList] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [calendarMode, setCalendarMode] = useState(1);
  const [dispMonth, setDispMonth] = useState('');
  const [pageName, setPageName] = useState('');
  const [backUrl, setBackUrl] = useState('');
  const [remainDisplay, setRemainDisplay] = useState(false);

  const calcedRsvStartDateRef = useRef('');

  // パンくずリスト設定から、ページ名・前の画面のURLをstateに設定
  const getBackUrlAndPageName = (topicPathConf) => {
    const locationPath = window.location.pathname;
    const tmpPath1 = locationPath.replace('/reserve/', '');
    const tmpPath2 = tmpPath1.replace('/test', '');
    const currentPath = tmpPath2.replace('/demo', '');

    let backUrl = '';
    let scnName = '';
    for (let i = 0; i < topicPathConf.length; i += 1) {
      const { url = '', scnName: tmpScnName = '' } = topicPathConf[i];
      if (currentPath === url) {
        // 「前に戻る」先のURL取得
        const { url: tmpBackUrl = '' } = topicPathConf[i - 1];
        if (tmpBackUrl === '/:lgCd') {
          backUrl = lgCd;
        } else {
          backUrl = tmpBackUrl;
        }

        // pageNameを取得
        scnName = tmpScnName;

        break;
      }
    }

    setPageName(scnName);
    setBackUrl(backUrl);
  };

  // 初期処理
  const init = async () => {
    // ページ名/戻る先URLの取得
    getBackUrlAndPageName(topicPath);

    const startDay = dayjs.utc().add(1, 'day').toISOString();
    // const startDay = isLoggedIn
    //   ? dayjs.utc().toISOString()
    //   : dayjs.utc().add(1, 'day').toISOString();
    const {
      weekInfo: resultWeekInfo = [],
      displayWeeks: resultDisplayWeeks = 1,
      daysList = [],
      calendarMode = 1,
      remainDisplay = '',
      calcedRsvStartDate = ''
    } = await selectDate.getCalendarData(lgCd, startDay, !!isLoggedIn, rsvSetId).catch((err) => {
      window.alert('カレンダーの取得に失敗しました。');
      handleError(err);
    });

    setWeekInfo(resultWeekInfo);
    setDisplayWeeks(resultDisplayWeeks);
    setDaysList(daysList);
    setCalendarMode(calendarMode);
    setRemainDisplay(remainDisplay);
    calcedRsvStartDateRef.current = calcedRsvStartDate;
  };

  // mount後に一度行う処理
  useDidMount(() => {
    // この画面に入った時点で前回の日付・時間選択をリセットする
    // → SelectionSummary の「利用日」「利用時間」欄が非表示になる
    dispatch(clearDate());
    init();
  });

  // カレンダー押下時の処理
  const moveToSelectTime = (info) => {
    const { date = '', dayOfWeek = '', remaining = 0 } = info;

    if (remaining > 0) {
      dispatch(saveTimearea({ rsvDate: date, rsvDayOfWeek: dayOfWeek }));
      navigate('/time');
    }
  };

  // 次月表示するかの判断
  const isActiveNextMonthBtn = () => {
    const endOfTheMonth = dayjs(weekInfo[7]?.date).endOf('month').format('YYYY-MM-DD');
    console.log(endOfTheMonth);

    return dayjs()
      .add(7 * displayWeeks, 'day')
      .isAfter(endOfTheMonth, 'day');
  };

  // 次月ボタン押下時処理
  const getNextMonth = async (date) => {
    if (!isActiveNextMonthBtn()) return;

    const startDay = dayjs.utc(date).add(1, 'month').toISOString();
    const lastDateOfPrevCalender = weekInfo[weekInfo.length - 1].date;
    const prevCalcedRsvStartDate = calcedRsvStartDateRef.current;
    const { weekInfo: resultWeekInfo = [], calcedRsvStartDate = '' } = await selectDate
      .getCalendarData(lgCd, startDay, !!isLoggedIn, rsvSetId, lastDateOfPrevCalender, prevCalcedRsvStartDate)
      .catch((err) => {
        window.alert('カレンダーの取得に失敗しました。');
        handleError(err);
      });
    setWeekInfo(resultWeekInfo);
    calcedRsvStartDateRef.current = calcedRsvStartDate;
  };

  // 前月表示するかの判断
  const isActiveBeforeMonthBtn = () => {
    const beginningOfTheMonth = dayjs(weekInfo[7]?.date).startOf('month').format('YYYY-MM-DD');

    return dayjs().add(1, 'day').isBefore(beginningOfTheMonth, 'day');
  };

  // 前月ボタン押下時処理
  const getBeforeMonth = async (date) => {
    if (!isActiveBeforeMonthBtn()) return;

    const startDay = dayjs.utc(date).subtract(1, 'month').toISOString();
    const lastDateOfPrevCalender = weekInfo[weekInfo.length - 1].date;
    const prevCalcedRsvStartDate = calcedRsvStartDateRef.current;
    const { weekInfo: resultWeekInfo = [], calcedRsvStartDate = '' } = await selectDate
      .getCalendarData(lgCd, startDay, !!isLoggedIn, rsvSetId, lastDateOfPrevCalender, prevCalcedRsvStartDate)
      .catch((err) => {
        window.alert('カレンダーの取得に失敗しました。');
        handleError(err);
      });
    setWeekInfo(resultWeekInfo);
    calcedRsvStartDateRef.current = calcedRsvStartDate;
  };

  // カレンダー表示内容を作成
  const createCalendar = () => {
    const dispMonth = dayjs(weekInfo[7]?.date).startOf('month').format('YYYY年M月');

    const weeks = [];
    for (let i = 0; i < 6; i += 1) {
      const days = [];
      for (let j = 7 * i; j < 7 * i + 7; j += 1) {
        const aDay = weekInfo[j];
        const { dispDate = '', rsvStat = '', className = '' } = aDay;

        if (className.match(/possible/) || className.match(/few/)) {
          days.push(
            <td key={`day_${j}`} className={className}>
              <a
                role="button"
                // href="javascript:void(0);"
                onClick={() => moveToSelectTime(aDay)}
                tabIndex={0}
              >
                {dispDate}
                <span>{rsvStat}</span>
              </a>
            </td>
          );
        } else {
          days.push(
            <td key={`day_${j}`} className={className}>
              {/* <a href="javascript:void(0);">
                {dispDate}
                <span>{rsvStat}</span>
              </a> */}
              <span
                style={{
                  textDecoration: 'none',
                  padding: '10px 0',
                  display: 'block',
                  width: '100%',
                  fontSize: 'inherit',
                }}
              >
                {dispDate}
                <span>{rsvStat}</span>
              </span>
            </td>
          );
        }
      }

      weeks.push(
        <tr key={`week_${i}`} className="day">
          {days}
        </tr>
      );
    }

    setCalendar([...weeks]);
    setDispMonth(dispMonth);
  };

  // weekInfo, displayWeeks, daysList更新時の処理
  useEffect(() => {
    if (weekInfo.length === 0) {
      return;
    }
    if (daysList.length === 0) {
      return;
    }
    createCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekInfo, displayWeeks, daysList]);

  // 空き状況の説明部分を作成
  const createRemainExplain = () => {
    if (remainDisplay) {
      return (
        <p id="reserve_text">
          <span className="possible">◯</span> 予約可能　<span className="few">△</span> 残りわずか
          <span className="reserved">×</span> 予約済み
          <span className="notset">&nbsp;&nbsp;&nbsp;&nbsp;</span> 予約不可
        </p>
      );
    }
    return (
      <p id="reserve_text">
        <span className="possible">◯</span> 予約可能　<span className="reserved">×</span> 予約済み
        <span className="notset">&nbsp;&nbsp;&nbsp;&nbsp;</span> 予約不可
      </p>
    );
  };

  return (
    <>
      <TopicPath />
      <SelectionSummary />
      <main>
        <article>
          <div id="contents">
            <MoveTop lgCd={lgCd} />
            <div className="inner630">
              <h2>{pageName}</h2>
              <p className="txtCenter">利用日を選択してください。</p>
              <div id="calendarBox">
                <ul id="calendarNavi" className="row jcSB">
                  <li id={isActiveBeforeMonthBtn() ? 'prev' : 'prev-disabled'}>
                    <a
                      role="button"
                      // href="javascript:void(0);"
                      onClick={() => getBeforeMonth(weekInfo[7]?.date)}
                      tabIndex={0}
                    >
                      前月
                    </a>
                  </li>
                  <li id="month" className="row">
                    {dispMonth}
                  </li>
                  <li id={isActiveNextMonthBtn() ? 'next' : 'next-disabled'}>
                    <a
                      role="button"
                      // href="javascript:void(0);"
                      onClick={() => getNextMonth(weekInfo[7]?.date)}
                      tabIndex={0}
                    >
                      次月
                    </a>
                  </li>
                </ul>
                <table
                  className={calendarMode === 1 ? 'calendar sundayStart' : 'calendar mondayStart'}
                >
                  <thead>
                    <tr className="day">
                      {daysList.map((value) => (
                        <th>{value}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>{calendar}</tbody>
                </table>
                {createRemainExplain()}
              </div>
              <p id="navi" className="row jcC">
                <BackButton url={`/${backUrl}`} />
              </p>
            </div>
            <div className='cancelAnnounce'>{CANCEL_ANNOUNCE}</div>
          </div>
        </article>
      </main>
    </>
  );
};

export default SelectDate;
